import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  AXIS_TICK,
  CHART,
  CHART_MARGIN,
  FUTURE_DASH,
  GRID_PROPS,
  TIME_SYNC_ID,
  Y_AXIS_WIDTH,
  formatDayLong,
  formatMonthTick,
  monthStartTicks,
} from '../../config/chartTheme';
import { firstFutureTs, type TimeRow } from './timeRows';

export interface BrushRange {
  startIndex: number;
  endIndex: number;
}

export default function OccupancyTimeline({
  rows,
  showReference,
  height = 260,
}: {
  rows: TimeRow[];
  /** Gesamtstadt als Vergleichsfläche – nur sinnvoll, wenn gefiltert wird. */
  showReference: boolean;
  height?: number;
}) {
  const futureStart = firstFutureTs(rows);
  const ticks = monthStartTicks(rows.map(r => r.iso), rows.length > 400 ? 2 : 1);

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={rows} margin={CHART_MARGIN} syncId={TIME_SYNC_ID}>
        <CartesianGrid {...GRID_PROPS} vertical={false} />

        {/* Zukunft abgrenzen: rechts davon steht kein Ist-Zustand, sondern was
            die Plattform heute für später ausweist. Ohne x2 füllt recharts bis
            zum rechten Rand. `hidden` statt des Vorgabewerts `discard`, sonst
            verschwindet die Fläche, sobald man in die Vergangenheit zoomt. */}
        {futureStart != null && (
          <ReferenceArea x1={futureStart} fill={CHART.foreground} fillOpacity={0.03} ifOverflow="hidden" />
        )}
        {futureStart != null && (
          <ReferenceLine x={futureStart} stroke={CHART.axis} strokeWidth={1} ifOverflow="hidden" />
        )}

        <XAxis
          dataKey="ts"
          type="number"
          scale="time"
          domain={['dataMin', 'dataMax']}
          ticks={ticks}
          tickFormatter={formatMonthTick}
          padding="gap"
          tick={AXIS_TICK}
          stroke={CHART.axis}
          tickLine={false}
        />
        <YAxis
          domain={[0, 100]}
          unit=" %"
          tick={AXIS_TICK}
          stroke={CHART.axis}
          tickLine={false}
          width={Y_AXIS_WIDTH}
        />
        <Tooltip content={<OccupancyTooltip showReference={showReference} />} />
        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 4 }} iconType="plainline" iconSize={14} />

        {showReference && (
          <Area
            type="linear"
            dataKey="referencePct"
            name="Gesamtstadt"
            stroke={CHART.reference}
            strokeWidth={1.5}
            fill={CHART.reference}
            fillOpacity={0.25}
            isAnimationActive={false}
            activeDot={false}
          />
        )}
        {/* `linear`, nicht `monotone`: eine geglättete Kurve erfindet zwischen
            zwei Tageswerten Zwischenwerte, die es nicht gibt.
            Kein `connectNulls` — sonst wird über Datenlücken hinweggezeichnet. */}
        <Line
          type="linear"
          dataKey="observedPct"
          name="beobachtet"
          stroke={CHART.occupancy}
          strokeWidth={2}
          dot={false}
          isAnimationActive={false}
        />
        <Line
          type="linear"
          dataKey="announcedPct"
          name="angekündigt"
          stroke={CHART.occupancy}
          strokeWidth={2}
          strokeDasharray={FUTURE_DASH}
          dot={false}
          isAnimationActive={false}
        />

      </ComposedChart>
    </ResponsiveContainer>
  );
}

function OccupancyTooltip({
  active,
  payload,
  showReference,
}: {
  active?: boolean;
  payload?: { payload: TimeRow }[];
  showReference?: boolean;
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const value = row.observedPct ?? row.announcedPct;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm px-3 py-2 text-xs">
      <div className="font-semibold text-gray-700">
        {formatDayLong(row.iso)}
        <span className="ml-2 font-normal text-gray-400">
          {row.isFuture ? 'angekündigt' : 'beobachtet'}
        </span>
      </div>
      {value == null ? (
        <div className="mt-1 text-gray-500">Keine Angabe der Plattform für diesen Tag</div>
      ) : (
        <>
          <div className="mt-1 text-gray-700">
            Belegung {String(value).replace('.', ',')} %
          </div>
          {showReference && row.referencePct != null && (
            <div className="text-gray-500">
              Gesamtstadt {String(row.referencePct).replace('.', ',')} %
            </div>
          )}
          {/* Die Datenbasis gehört an jeden Wert: ein Tag mit n=1 sieht sonst
              genauso belastbar aus wie einer mit n=512. */}
          <div className="mt-1 text-gray-400">
            Datenbasis: {row.n} {row.n === 1 ? 'Inserat' : 'Inserate'}
          </div>
        </>
      )}
    </div>
  );
}
